import React, { useEffect, useMemo, useState } from 'react';
import { TeamReview as TeamReviewModel } from 'models/projects/TeamReview';
import { MemberVote } from 'models/projects/MemberVote';
import { Vote } from 'models/projects/Vote';
import { TeamReviewMemberOrderSetting } from 'models/teams/teamReview/TeamReviewMemberOrderSetting';
import { utilities } from "utils/utilities";
import { TeamReviewMemberOrder } from 'models/teams/teamReview/TeamReviewMemberOrder';
import { ApiService } from 'services/ApiService';
import { TeamReviewDecision } from 'models/projects/TeamReviewDecision';

type Props = {
    teamReview: TeamReviewModel | null;
    className?: string;
    isOwner?: boolean;
    isTeamMember?: boolean;
    onVote?: (vote: MemberVote) => void;
    projectId: string;
}

const TeamReviewComponent: React.FC<Props> = ({ teamReview, className = '', isOwner = false, isTeamMember = false, onVote, projectId }) => {
    const currentUserId = utilities.getUserId();
    const [localReview, setLocalReview] = useState<TeamReviewModel | null>(teamReview);
    const [denyModalOpen, setDenyModalOpen] = useState(false);
    const [denyReason, setDenyReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [voteError, setVoteError] = useState('');

    useEffect(() => setLocalReview(teamReview), [teamReview]);

    // build a member list from settings.order and memberVotes
    const members = useMemo(() => {
        const order: TeamReviewMemberOrder[] = localReview?.teamReviewSettings?.teamMemberOrder ?? [];
        const votes: MemberVote[] = localReview?.memberVotes ?? [];
        return order.map(o => {
            const vote = votes.find(v => v.teamMemberId === o.teamMemberId) ?? null;
            return {
                memberId: o.teamMemberId,
                displayName: `${o.firstName || ''} ${o.lastName || ''}`.trim() || o.teamMemberId,
                position: o.position,
                vote,
            };
        });
    }, [localReview]);

    if (!localReview) {
        return (
            <div className={`p-4 bg-white border rounded shadow-sm ${className}`}>
                <h3 className="text-base font-semibold mb-2">Team Review</h3>
                <p>No team review available.</p>
            </div>
        );
    }

    const totalMembers = members.length;
    const approvals = members.filter(m => m.vote && m.vote.vote === Vote.Pass).length;
    const denials = members.filter(m => m.vote && m.vote.vote === Vote.Deny).length;
    const myMemberEntry = members.find(m => m.memberId === currentUserId) ?? null;
    // consider any non-None vote as having voted
    const myVote = (myMemberEntry?.vote?.vote ?? Vote.None) !== Vote.None;

    const activeVoterId = (() => {
        if (localReview.teamReviewSettings?.teamReviewMemberOrderSetting === TeamReviewMemberOrderSetting.Sequential) {
            const orderedMembers = members.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
            const firstPending = orderedMembers.find(m => m.vote?.vote === Vote.None);
            return firstPending?.memberId ?? null;
        }
        return null;
    })();

    const canVote = () => {
        if (!isTeamMember && !isOwner) return false;
        if (!myMemberEntry) return false;
        if (myVote) return false; // already voted
        if (localReview.decision === TeamReviewDecision.Approved) return false;
        if (localReview.teamReviewSettings?.teamReviewMemberOrderSetting === TeamReviewMemberOrderSetting.Sequential) {
            return activeVoterId === currentUserId;
        }
        return true;
    };

    const handleApprove = async () => {
        if (!myMemberEntry) return;
        const vote: MemberVote = {
            teamMemberId: currentUserId || myMemberEntry.memberId,
            dateVoted: new Date(),
            teamReviewId: localReview.teamReviewId,
            projectId: projectId,
            vote: Vote.Pass,
            position: myMemberEntry.position ?? 0,
            decisionReason: '',
        };

        setSubmitting(true);
        setVoteError('');
        try {
            await ApiService.submitTeamMemberReviewVote(vote);
            setLocalReview(prev => {
                if (!prev) return prev;
                const updated = { ...prev } as TeamReviewModel;
                updated.memberVotes = updated.memberVotes.filter(v => v.teamMemberId !== vote.teamMemberId).concat(vote as any);
                return updated;
            });
        } catch (error: any) {
            const statusCode = error?.response?.status;
            const apiMessage = error?.response?.data?.errors?.[0]?.message;

            if (statusCode === 400) {
                setVoteError(apiMessage || 'This review is already approved and cannot be voted on again.');
            } else {
                setVoteError(apiMessage || 'Unable to submit vote right now. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDenyClick = () => {
        setDenyModalOpen(true);
    };

    const submitDeny = async () => {
        if (!myMemberEntry) return;
        const vote: MemberVote = {
            teamMemberId: currentUserId || myMemberEntry.memberId,
            dateVoted: new Date(),
            projectId: projectId,
            teamReviewId: localReview.teamReviewId,
            vote: Vote.Deny,
            position: myMemberEntry.position ?? 0,
            decisionReason: denyReason || 'No reason provided',
        };

        setSubmitting(true);
        setVoteError('');
        try {
            await ApiService.submitTeamMemberReviewVote(vote);
            setLocalReview(prev => {
                if (!prev) return prev;
                const updated = { ...prev } as TeamReviewModel;
                updated.memberVotes = updated.memberVotes.filter(v => v.teamMemberId !== vote.teamMemberId).concat(vote as any);
                return updated;
            });
            setDenyModalOpen(false);
            setDenyReason('');
        } catch (error: any) {
            const statusCode = error?.response?.status;
            const apiMessage = error?.response?.data?.errors?.[0]?.message;

            if (statusCode === 400) {
                setVoteError(apiMessage || 'This review is already approved and cannot be voted on again.');
            } else {
                setVoteError(apiMessage || 'Unable to submit vote right now. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const isPassed = (() => {
        const setting = localReview.teamReviewSettings?.teamReviewApprovalSetting;
        if (setting === undefined) return false;
        if (setting === 0) { // AllPass
            return approvals === totalMembers && denials === 0 && totalMembers > 0;
        }
        // AnyPass
        return approvals > 0;
    })();

    return (
        <div className={`p-4 bg-white border rounded shadow-sm ${className}`}>
            <h3 className="text-base font-semibold mb-2">Team Review</h3>

            <div className="mb-3 text-sm">
                <strong>Votes:</strong> {approvals} approved • {denials} denied • {totalMembers} members
            </div>

            <div className="mb-4">
                <div className="flex gap-2">
                    <button disabled={!canVote() || submitting} onClick={handleApprove} className={`px-3 py-1 rounded bg-green-600 text-white disabled:opacity-50`}>Approve</button>
                    <button disabled={!canVote() || submitting} onClick={handleDenyClick} className={`px-3 py-1 rounded bg-red-600 text-white disabled:opacity-50`}>Deny</button>
                </div>
                {localReview.decision === TeamReviewDecision.Approved && (
                    <p className="mt-2 text-xs text-gray-600">Review is approved. Additional votes are not allowed.</p>
                )}
                {voteError && (
                    <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{voteError}</p>
                )}
                {localReview.teamReviewSettings?.teamReviewMemberOrderSetting === TeamReviewMemberOrderSetting.Sequential && (
                    <div className="mt-2 text-xs text-gray-600">Sequential voting enabled. Active voter: {activeVoterId ?? 'none'}</div>
                )}
            </div>

            <div className="mb-2">
                <ul className="divide-y">
                    {members.map(m => (
                        <li key={m.memberId} className={`py-2 flex items-start justify-between`}>
                            <div>
                                <div className="text-sm font-medium">{m.displayName}</div>
                                <div className="text-xs text-gray-500">position: {m.position}</div>
                            </div>
                            <div className="text-right text-sm">
                                {m.vote ? (
                                    <div className={`px-2 py-1 rounded ${m.vote.vote === Vote.Pass ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>{m.vote.vote === Vote.Pass ? 'Pass' : 'Deny'}{m.vote.decisionReason ? ` — ${m.vote.decisionReason}` : ''}</div>
                                ) : (
                                    <div className={`px-2 py-1 rounded text-xs ${activeVoterId && activeVoterId !== m.memberId ? 'text-gray-400' : 'text-gray-600'}`}>{activeVoterId === m.memberId ? 'Waiting (your turn)' : 'Pending'}</div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="mt-3 text-sm">
                <strong>Decision:</strong> {isPassed ? <span className="text-green-600">Passed</span> : <span className="text-gray-600">Pending</span>}
            </div>

            {/* Deny modal (simple) */}
            {denyModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded p-4 w-96">
                        <h4 className="font-semibold mb-2">Provide reason for denial</h4>
                        <textarea value={denyReason} onChange={e => setDenyReason(e.target.value)} className="w-full p-2 border rounded mb-2" rows={4} />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setDenyModalOpen(false); setDenyReason(''); }} className="px-3 py-1 rounded border">Cancel</button>
                            <button onClick={submitDeny} disabled={submitting} className="px-3 py-1 rounded bg-red-600 text-white">Submit Deny</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TeamReviewComponent;